import { type Currency, getConfig } from '@/constants/config';
import { nonBreakingSpace } from '@/constants/specialCharacters';
import { removeTrailingZeros } from '@/localization/utils';
import { assertCondition } from '@/utils/typescript';

export type FormatOptions = {
  displayTrailingZeros?: boolean;
  displaySign?: boolean;
  displayDecimals?: boolean;
};

const initialFormatOptions: FormatOptions = {
  displaySign: true,
  displayTrailingZeros: false,
  displayDecimals: true,
};

type FormatCurrencyOptions = FormatOptions & {
  currency: Currency;
};

export const formatCurrency = (amount: number, options: Partial<FormatCurrencyOptions> = {}): string => {
  const countryConfig = getConfig();

  const { currency, displaySign, displayTrailingZeros, displayDecimals } = {
    ...initialFormatOptions,
    currency: countryConfig.currency,
    ...options,
  };

  const fractionDigits = displayDecimals === false ? 0 : 2;

  const formatter = new Intl.NumberFormat(countryConfig.locale, {
    ...(displaySign
      ? {
          style: 'currency',
          currency,
        }
      : {}),
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    ...(displayDecimals === false ? { roundingMode: 'trunc' } : {}),
  });

  let formattedAmount = formatter.format(amount);
  if (displayDecimals && !displayTrailingZeros) {
    formattedAmount = removeTrailingZeros(formattedAmount);
  }

  assertCondition(formattedAmount !== undefined);

  return formattedAmount.replace(/ /g, nonBreakingSpace);
};
